import { UserRole, type IProfileUser } from '~~/shared/interfaces/profile';
import { useBaseStore } from '~/store/base.store';
import CookieNameSpace from '~/const/cookie';
import RouterNameSpace, { shouldSkipRoleHomePageRedirect } from '~/const/router';
import {
	addTokenInStorage,
	checkForNeedUpdateToken,
	getCachedProfile,
	getTokenFromStorage,
	normalizeProfile,
	removeCachedProfile,
	removeTokenFromStorage,
	saveCachedProfile
} from '~/helpers/storage';
import { clearOfflineRequests } from '~/helpers/offlineRequestQueue';
import { getConnectionStatus } from '~/helpers/network';
import { useNotificationStore } from '~/store/notification.store';

export const useProfileStore = defineStore('profile', () => {
	const baseStore = useBaseStore();
	const isGet401Error = ref(false);
	const isLoggedIn = ref(false);
	const profileInfo = ref<IProfileUser | null>(null);
	const isDriver = computed(() => profileSettings.value?.role === UserRole.Driver);
	const isStorekeeper = computed(() => profileSettings.value?.role === UserRole.StorekeeperPickUp);
	const isTrucker = computed(() => profileSettings.value?.role === UserRole.Trucker);
	const profileSettings = computed(() => {
		if (profileInfo.value?.role === UserRole.Admin) {
			return {
				defaultUrl: RouterNameSpace.routerRedirectIsLoggedIn,
				role: UserRole.Admin
			};
		} else if (profileInfo.value?.role === UserRole.StorekeeperPickUp) {
			return {
				defaultUrl: '/storekeeper',
				role: UserRole.StorekeeperPickUp
			};
		} else if (profileInfo.value?.role === UserRole.Driver) {
			return {
				defaultUrl: '/cargos',
				role: UserRole.Driver
			};
		} else if (profileInfo.value?.role === UserRole.Trucker) {
			return {
				defaultUrl: '/trucker',
				role: UserRole.Trucker
			};
		}
	});

	const loadingEvents = reactive({
		btnSignIn: false,
		btnUpdateProfile: false,
		btnUpdateOrganizationLogo: false,
		btnUpdateProfileLogo: false
	});

	const router = useRouter();

	function $reset() {
		profileInfo.value = null;
	}

	const restoreCachedProfile = async () => {
		if (profileInfo.value) return true;

		const cachedProfile = await getCachedProfile();
		if (!cachedProfile) {
			isLoggedIn.value = false;
			return false;
		}

		profileInfo.value = cachedProfile;
		isLoggedIn.value = true;
		return true;
	};

	const redirectToRoleHomePage = async () => {
		const targetPath = profileSettings.value?.defaultUrl;
		const roleHomePages = ['/cargos', '/storekeeper', '/trucker'];
		const currentPath = router.currentRoute.value.path.replace(/\/+$/, '') || '/';
		const shouldSkipRedirect = shouldSkipRoleHomePageRedirect(
			profileSettings.value?.role,
			currentPath
		);

		const isRoleHomePage = roleHomePages.some(
			(path) => currentPath === path || currentPath.startsWith(`${path}/`) || currentPath.endsWith(path)
		);

		if (
			targetPath &&
			(isRoleHomePage || currentPath === '/') &&
			!shouldSkipRedirect &&
			currentPath !== targetPath
		) {
			await router.replace(targetPath);
		}
	};

	const getProfile = async (forceRefresh = false) => {
		const token = await getTokenFromStorage(CookieNameSpace.getAccessToken);
		if (!token || (!forceRefresh && !(await getConnectionStatus()))) {
			await restoreCachedProfile();
			return;
		}

		const { data, status, error } = await useCustomFetch<IProfileUser>(
			'/api/Auth/profile',
			{
				headers: { token },
				timeout: 5000
			},
			{ isDisabledErrroMessage: true, isSilent: true, isNeedOffline: false }
		);

		if (status.value === 'success' && data.value) {
			const normalizedProfile = normalizeProfile(data.value);
			if (!normalizedProfile) {
				console.error('Profile response has an invalid format and cannot be cached.');
				await restoreCachedProfile();
				return;
			}

			profileInfo.value = normalizedProfile;
			isLoggedIn.value = true;
			try {
				await saveCachedProfile(normalizedProfile);
			} catch (cacheError) {
				console.error('Failed to cache profile:', cacheError);
			}
			try {
				const persistedProfile = await getCachedProfile();
				if (!persistedProfile) {
					throw new Error('Profile cache verification failed after saving.');
				}
			} catch (cacheError) {
				console.error('Failed to verify cached profile:', cacheError);
			}
			return;
		}

		const requestError = error.value as { statusCode?: number; response?: { status?: number } } | null;
		const responseStatus = requestError?.response?.status || requestError?.statusCode;
		if (status.value === 'error' && responseStatus === 401) {
			profileInfo.value = null;
			isLoggedIn.value = false;
			await removeTokenFromStorage(CookieNameSpace.getAccessToken);
			return;
		}

		await restoreCachedProfile();

		// todo check after time
		// try {
		// 	if (checkForNeedUpdateToken()) {
		// 		await refreshHandler();
		// 	}
		// } catch {}
	};

	const checkToken = async () => {
		const token = await getTokenFromStorage(CookieNameSpace.getAccessToken);
		if (!token) return;
		const { data, status } = await useCustomFetch<IProfileUser>(
			'/api/Auth/check',
			{
				headers: {
					token: token
				},
				timeout: 4000
			},
			{ isDisabledErrroMessage: true, isSilent: true, isNeedOffline: false }
		);
		if (status.value === 'success') {
			try {
				if (await checkForNeedUpdateToken()) {
					await refreshHandler();
				}
			} catch {}
		}
	};

	const updateProfile = async (profile: IProfileUser) => {
		// const { data, status } = await useCustomFetch<IProfileUser>(
		// 	'/api/Employees/update',
		// 	{
		// 		method: 'put',
		// 		body: { ...profile },
		// 		watch: false
		// 	},
		// 	{
		// 		setLoading: (load: boolean) => {
		// 			loadingEvents.btnUpdateProfile = load;
		// 		}
		// 	}
		// );
		// if (status.value === 'success') {
		// 	profileInfo.value = data.value;
		// 	useNotificationStore().addNewAlert('Данные профиля успешно обновлены', 2500, AlertsTypes.SUCCESS);
		// }
	};

	const authUser = async ({ login, password }: any) => {
		loadingEvents.btnSignIn = true;

		const { data, status } = await useCustomFetch<any>(
			'/api/Auth/login',
			{
				method: 'post',
				query: { login: login, password: password },
				server: false,
				watch: false
			},
			{ isDisabledLogoutAction: true }
		);

		if (status.value === 'success' && data.value) {
			await removeCachedProfile();
			await clearOfflineRequests();
			profileInfo.value = null;
			isLoggedIn.value = false;
			data.value?.token &&
				(await addTokenInStorage(CookieNameSpace.getAccessToken, data.value.token, data.value?.dateEnd));
			await getProfile(true);
			if (isLoggedIn.value) await redirectOnMainPage();
		} else if (true) {
		}
		loadingEvents.btnSignIn = false;
	};

	const redirectOnMainPage = async () => {
		const targetPath = profileSettings.value?.defaultUrl;
		if (!targetPath) return;
		await router.replace(targetPath);
	};

	const logoutHandler = async (errorMessage?: string) => {
		try {
			isGet401Error.value = true;
			const token = await getTokenFromStorage(CookieNameSpace.getAccessToken);
			isGet401Error.value = false;
			const { data, status } = await useCustomFetch<any>('/api/Auth/logout', {
				method: 'post',
				server: false,
				query: { token },
				onResponseError: () => {}
			});

			if (status.value) {
				await removeTokenFromStorage(CookieNameSpace.getAccessToken);
				isLoggedIn.value = false;
				router.push(RouterNameSpace.routerRedirectNoLoggedIn);

				setTimeout(() => {
					const resetStore = useResetStore();
					resetStore.all();
				});
			}
		} catch {
			isLoggedIn.value = false;
			if (errorMessage) {
				useNotificationStore().addNewAlert(errorMessage);
			}
		}
	};

	const refreshHandler = async () => {
		const token = await getTokenFromStorage(CookieNameSpace.getAccessToken);

		await useCustomFetch<any>(
			'/api/Auth/refresh',
			{
				method: 'post',
				watch: false,
				server: false,
				retry: false,
				body: JSON.stringify(token),
				onResponse: async ({ response }) => {
					baseStore.isLoading = false;
					if (response.status >= 400) return;
					response._data.token &&
						(await addTokenInStorage(CookieNameSpace.getAccessToken, response._data.token, response._data?.dateEnd));
				}
			},
			{
				isDisabledErrroMessage: true
			}
		);
	};

	return {
		profileInfo,
		isLoggedIn,
		loadingEvents,
		isGet401Error,
		profileSettings,
		isDriver,
		isStorekeeper,
		isTrucker,
		$reset,
		restoreCachedProfile,
		getProfile,
		checkToken,
		authUser,
		logoutHandler,
		refreshHandler,
		updateProfile,
		redirectToRoleHomePage,
		redirectOnMainPage
	};
});
